export const ABORT_CODES = [
  'cancelled',
  'deadline-exceeded',
  'disposed',
] as const

export type AbortCode = (typeof ABORT_CODES)[number]

export type AbortReason = {
  readonly code: AbortCode
  readonly message: string
  readonly cause?: unknown
}

export type LinkedAbortController = {
  readonly signal: AbortSignal
  abort(reason?: AbortReason): void
  dispose(): void
}

export function createAbortReason(
  code: AbortCode,
  message: string,
  cause?: unknown
): AbortReason {
  if (cause === undefined) {
    return Object.freeze({ code, message })
  }

  return Object.freeze({ code, message, cause })
}

export function isAbortReason(value: unknown): value is AbortReason {
  if (typeof value !== 'object' || value === null) {
    return false
  }

  const candidate = value as Partial<AbortReason>

  return (
    typeof candidate.message === 'string' &&
    ABORT_CODES.some((code) => code === candidate.code)
  )
}

export function getAbortReason(signal: AbortSignal): AbortReason | undefined {
  if (!signal.aborted) {
    return undefined
  }

  if (isAbortReason(signal.reason)) {
    return signal.reason
  }

  return createAbortReason(
    'cancelled',
    'The operation was cancelled.',
    signal.reason
  )
}

export function linkAbortSignals(
  signals: readonly (AbortSignal | undefined)[]
): LinkedAbortController {
  const controller = new AbortController()
  const removers: Array<() => void> = []
  let disposed = false

  const removeListeners = (): void => {
    while (removers.length > 0) {
      removers.pop()?.()
    }
  }

  const abort = (
    reason = createAbortReason('cancelled', 'The operation was cancelled.')
  ): void => {
    if (controller.signal.aborted) {
      return
    }

    controller.abort(reason)
    removeListeners()
  }

  for (const signal of signals) {
    if (signal === undefined) {
      continue
    }

    if (signal.aborted) {
      abort(getAbortReason(signal))
      break
    }

    const handleAbort = (): void => {
      abort(getAbortReason(signal))
    }

    signal.addEventListener('abort', handleAbort, { once: true })
    removers.push(() => signal.removeEventListener('abort', handleAbort))
  }

  return {
    signal: controller.signal,
    abort,
    dispose(): void {
      if (disposed) {
        return
      }

      disposed = true
      removeListeners()
    },
  }
}
